import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppToasterService } from '../../../core/services/toaster.service';
import { AdminPageShellComponent } from '../../../shared/components/admin/admin-page-shell/admin-page-shell.component';
import { CtaButtonComponent } from '../../../shared/components/ui/cta-button/cta-button.component';
import {
  AdminPlan,
  BillingAdminService,
  PlanLimit,
  PlanLimitCatalogEntry,
  PlanLimitState,
} from './billing-admin.service';

interface DraftCell {
  state: PlanLimitState;
  value: number;
  subject: string;
  period: string;
}

const GROUP_ORDER = [
  'Family & access',
  'AI assistant',
  'Duties',
  'XP',
  'Commerce & add-ons',
] as const;

@Component({
  selector: 'app-billing-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    MatProgressSpinnerModule,
    AdminPageShellComponent,
    CtaButtonComponent,
  ],
  templateUrl: './billing-admin.component.html',
  styleUrl: './billing-admin.component.scss',
})
export class BillingAdminComponent implements OnInit {
  #billing = inject(BillingAdminService);
  #toaster = inject(AppToasterService);
  #translate = inject(TranslateService);

  readonly isLoading = signal(true);
  readonly hasError = signal(false);
  readonly isSaving = signal(false);
  readonly plans = signal<AdminPlan[]>([]);
  readonly catalog = signal<PlanLimitCatalogEntry[]>([]);
  /** planCode → featureKey → draft */
  readonly drafts = signal<Record<string, Record<string, DraftCell>>>({});

  readonly planColumns = computed(() =>
    [...this.plans()].sort((a, b) => a.sortOrder - b.sortOrder),
  );

  readonly groups = computed(() => {
    const map = new Map<string, PlanLimitCatalogEntry[]>();
    for (const entry of this.catalog()) {
      const list = map.get(entry.group) ?? [];
      list.push(entry);
      map.set(entry.group, list);
    }
    const order = new Map<string, number>(
      GROUP_ORDER.map((group, index) => [group, index]),
    );
    return [...map.entries()].sort(
      ([left], [right]) =>
        (order.get(left) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(right) ?? Number.MAX_SAFE_INTEGER),
    );
  });

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    this.isLoading.set(true);
    this.hasError.set(false);
    try {
      const [plans, catalog] = await Promise.all([
        this.#billing.listPlans(),
        this.#billing.limitCatalog(),
      ]);
      this.plans.set(plans);
      this.catalog.set(catalog);
      this.drafts.set(this.#buildDrafts(plans, catalog));
    } catch {
      this.hasError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  cell(planCode: string, key: string): DraftCell | undefined {
    return this.drafts()[planCode]?.[key];
  }

  setState(planCode: string, key: string, state: PlanLimitState): void {
    this.#patchCell(planCode, key, {
      state,
      value:
        state === 'disabled' ? 0 : state === 'unlimited' ? -1 : Math.max(1, this.cell(planCode, key)?.value || 1),
    });
  }

  setValue(planCode: string, key: string, raw: string | number): void {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n)) return;
    this.#patchCell(planCode, key, {
      state: 'limited',
      value: Math.trunc(n),
    });
  }

  setSubject(planCode: string, key: string, subject: string): void {
    this.#patchCell(planCode, key, { subject });
  }

  setPeriod(planCode: string, key: string, period: string): void {
    this.#patchCell(planCode, key, { period });
  }

  async savePlan(planCode: string): Promise<void> {
    if (this.isSaving()) return;
    const draft = this.drafts()[planCode];
    if (!draft) return;

    const limits: PlanLimit[] = Object.entries(draft).map(([key, cell]) => {
      const entry = this.catalog().find((c) => c.key === key);
      return {
        key,
        state: cell.state,
        value: cell.value,
        meter: entry?.isCapability
          ? undefined
          : { subject: cell.subject, period: cell.period },
      };
    });

    this.isSaving.set(true);
    try {
      const updated = await this.#billing.updatePlanLimits(planCode, limits);
      this.plans.update((rows) =>
        rows.map((p) => (p.code === planCode ? { ...p, ...updated } : p)),
      );
      this.#toaster.success(
        this.#translate.instant('app.admin.billing.saved', { plan: planCode }),
      );
    } catch {
      this.#toaster.error(
        this.#translate.instant('app.admin.billing.saveError'),
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  #patchCell(
    planCode: string,
    key: string,
    patch: Partial<DraftCell>,
  ): void {
    this.drafts.update((all) => {
      const planDraft = { ...(all[planCode] ?? {}) };
      planDraft[key] = { ...planDraft[key], ...patch };
      return { ...all, [planCode]: planDraft };
    });
  }

  #buildDrafts(
    plans: AdminPlan[],
    catalog: PlanLimitCatalogEntry[],
  ): Record<string, Record<string, DraftCell>> {
    const out: Record<string, Record<string, DraftCell>> = {};
    for (const plan of plans) {
      const byKey = new Map((plan.limits ?? []).map((l) => [l.key, l]));
      out[plan.code] = {};
      for (const entry of catalog) {
        const existing = byKey.get(entry.key);
        const state = (existing?.state as PlanLimitState) || 'disabled';
        out[plan.code][entry.key] = {
          state,
          value: existing?.value ?? 0,
          subject:
            existing?.meter?.subject ||
            entry.defaultMeter?.subject ||
            entry.allowedSubjects[0] ||
            'family',
          period:
            existing?.meter?.period ||
            entry.defaultMeter?.period ||
            entry.allowedPeriods[0] ||
            'none',
        };
      }
    }
    return out;
  }
}
